// Builds the FINAL docxtpl render context on the frontend, so the studentless
// individual-documents endpoint only has to drop it into the template (no
// Document, no DocumentFieldValue, no improve_context_rendering on the server).
//
// This is a faithful port of DocumentOnline.improve_context_rendering +
// the get_context() defaults it builds on (backend/api/models/documents.py):
// it walks the template fields and resolves each answered field into the exact
// shape the .docx template expects, using the display data the field
// serializer already ships to the modal (options[].text, table_titles,
// oas/oas_options, special_select_options, ...) plus, at the end, the `extras`
// block (school-specific "dimensiones" grouped by area header). Keep it in sync
// with the backend method.
//
// Two kinds — DOCUMENT_CHECKBOX_ACHIEVEMENT (8) and OA_ACHIEVEMENT (9) — are
// response-coupled: their options are serialized only for an existing
// DocumentOnline, so they come through empty for a blank template and cannot
// be answered studentless. They resolve to [] here (same as the modal can't
// render them), which is a fundamental data-model limitation, not a TODO.

const KIND = {
  NON_INTERACTABLE: 0,
  STRING: 1,
  BOOL: 2,
  USER_CHECKBOX_ACHIEVEMENT: 3,
  USER_SELECTOR: 4,
  SPECIAL_SELECTOR: 5,
  OA_FIELD: 6,
  CUSTOM_CHECKBOX: 7,
  DOCUMENT_CHECKBOX_ACHIEVEMENT: 8,
  OA_ACHIEVEMENT: 9,
  CUSTOM_RADIO: 10,
  TEXT: 11,
  OA_PROGRESSION: 12,
  TEXT_OPTIONS: 13,
  TEXT_EJES_ACHIEVEMENT: 14,
  SPECIAL_MULTI_SELECTOR: 15,
};

const FU_REEVAL = "FU de reevaluación online";

// Backend default when a field has no DocumentTemplateFieldTableTitle rows.
const DEFAULT_TABLE_TITLES = [
  "Logrado",
  "Parcialmente Logrado",
  "No logrado",
  "No trabajado en este período",
];
const CRI_PRO_POSITION_NAMES = [
  "Graduación del nivel de complejidad",
  "Priorización",
  "Temporalización",
  "Enriquecimiento del curriculum",
  "Eliminación del aprendizaje",
];

// Every active field of the template, whether it stores fields under
// pages -> rows -> fields (paged online) or rows -> fields (FUReev-style).
// Paged templates lazy-load rows per page in the modal; only visited pages
// have populated rows here, which is fine — unvisited pages are unanswered.
function iterFields(documentTemplate) {
  const out = [];
  const pages = (documentTemplate && documentTemplate.pages) || [];
  pages.forEach((page) => {
    (page.rows || []).forEach((row) => {
      (row.fields || []).forEach((f) => out.push(f));
    });
  });
  if (!pages.length) {
    ((documentTemplate && documentTemplate.rows) || []).forEach((row) => {
      (row.fields || []).forEach((f) => out.push(f));
    });
  }
  return out;
}

function findOption(options, id) {
  return (options || []).find((o) => o.id === id);
}

// Search the field's OA tags AND their nested `progressions` for a tag id —
// the OA_PROGRESSION `select` (adecuación) can reference a related-progression
// OA, which the backend resolves with CustomTestTag.objects.get(id=...).
function findTagDeep(oasList, id) {
  const list = oasList || [];
  for (let i = 0; i < list.length; i += 1) {
    const tag = list[i];
    if (!tag) continue;
    if (tag.id === id) return tag;
    const sub = findTagDeep(tag.progressions, id);
    if (sub) return sub;
  }
  return null;
}

function tableTitlesFor(field) {
  const titles = (field.table_titles || []).filter((t) => t != null);
  return titles.length ? titles : DEFAULT_TABLE_TITLES;
}

function groupBy(list, keyFn) {
  const grouped = {};
  list.forEach((item) => {
    const k = keyFn(item);
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(item);
  });
  return grouped;
}

function isTruthyBool(value) {
  return value === true || value === "True" || value === "true";
}

// --- django slugify port (for the `extras` area headers) ------------------
function stripAccents(s) {
  return (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "");
}
// Mirrors: slugify(strip_accents(text)).upper().replace('-', '_')
function slugifyUpper(text) {
  let s = stripAccents(text)
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "-");
  // strip leading/trailing hyphens (django slugify does)
  s = s.replace(/^-+|-+$/g, "");
  return s.toUpperCase().replace(/-/g, "_");
}

function buildOaEntry(field, kind, tagId, entry) {
  const tag = findOption(field.oas, tagId);
  if (!tag) return null;
  const data = Object.assign({}, tag);
  const options = field.oas_options || [];
  if (kind === KIND.OA_FIELD) {
    data.criteria = [];
    (entry.criteria || []).forEach((pos) => {
      if (typeof pos === "number" && pos >= 0 && pos < options.length) {
        data.criteria.push(options[pos]);
      }
    });
    data.target = entry.target || "";
  } else {
    // OA_PROGRESSION
    data.criPro = "";
    (entry.criPro || []).forEach((pos) => {
      if (typeof pos === "number" && pos >= 0 && pos < CRI_PRO_POSITION_NAMES.length) {
        data.criPro += CRI_PRO_POSITION_NAMES[pos] + "\n";
      }
    });
    if (entry.select) {
      const adec = findTagDeep(field.oas, entry.select);
      data.target = adec
        ? `${adec.grade} - ${adec.name} \n ${adec.description}`
        : entry.manual || "";
    } else {
      data.target = entry.manual || "";
    }
  }
  return data;
}

// Resolve one field's raw answer into its final docx shape (mirrors the
// per-kind branches of improve_context_rendering / get_context).
function resolveField(context, field, value, isReeval, customPros) {
  const key = field.key;
  const kind = field.kind;

  if (kind === KIND.USER_CHECKBOX_ACHIEVEMENT) {
    const titles = tableTitlesFor(field);
    const out = [];
    Object.keys(value || {}).forEach((optId) => {
      const pos = value[optId];
      if (pos == null) return;
      const opt = findOption(field.options, optId);
      if (opt && pos >= 0 && pos < titles.length) out.push([opt.text, titles[pos]]);
    });
    context[key] = out;
  } else if (kind === KIND.SPECIAL_SELECTOR) {
    context[key] = [];
    if (value) {
      const opt = findOption(field.special_select_options, value);
      if (opt && opt.data) {
        context[key] = Object.keys(opt.data).map((k) => [k, opt.data[k]]);
      }
    }
  } else if (kind === KIND.USER_SELECTOR) {
    // In single-use mode the user data is typed in (value is the data object);
    // in the normal flow value is a user id resolved from user_select_options.
    let user = null;
    let manual = false;
    if (value && typeof value === "object") {
      user = value;
      manual = true;
    } else if (value) {
      user = findOption(field.user_select_options, value);
    }
    if (user) {
      const clean = {};
      let filled = false;
      Object.keys(user).forEach((k) => {
        clean[k] = user[k] == null ? "" : user[k];
        if (k !== "id" && String(clean[k]).trim() !== "") filled = true;
      });
      // id-based selection always renders (faithful to the backend); a
      // manually-typed entry only renders if the user actually filled it.
      if (!manual || filled) {
        // PROFESSIONAL_*_APOYO (new fureev's second section) -> its own list.
        const prefix =
          key.split("_")[0] + (key.endsWith("_APOYO") ? "_APOYO" : "");
        if (!context[prefix]) context[prefix] = [];
        context[prefix].push(clean);
      }
    }
  } else if (kind === KIND.OA_FIELD || kind === KIND.OA_PROGRESSION) {
    const list = [];
    const entries = (value && value.values) || {};
    ((value && value.oas) || []).forEach((tagId) => {
      const data = buildOaEntry(field, kind, tagId, entries[tagId] || {});
      if (data) list.push(data);
    });
    context[key] = list;
    context["grouped_" + key] = groupBy(list, (item) => item.subject);
  } else if (kind === KIND.SPECIAL_MULTI_SELECTOR) {
    // value = array of option ids -> list of the options' data dicts.
    const entries = (Array.isArray(value) ? value : [])
      .map((id) => findOption(field.special_select_options, id))
      .filter((o) => o)
      .map((o) => Object.assign({}, o.data || {}));
    context[key] = entries;
    // PROF* keys are non-user professionals: merged after the loop into the
    // same list the USER_SELECTORs build (backend normalizes their keys).
    const upper = key.toUpperCase();
    if (upper.startsWith("PROF")) {
      const target = upper.endsWith("_APOYO")
        ? "PROFESSIONAL_APOYO"
        : "PROFESSIONAL";
      customPros[target] = (customPros[target] || []).concat(entries);
    }
  } else if (kind === KIND.CUSTOM_CHECKBOX) {
    const out = [];
    (value || []).forEach((optId) => {
      const opt = findOption(field.custom_checkbox_options, optId);
      if (opt) out.push(opt.text);
    });
    context[key] = out;
  } else if (kind === KIND.CUSTOM_RADIO) {
    context[key] = "";
    if (value) {
      const opt = findOption(field.custom_checkbox_options, value);
      if (opt) context[key] = opt.text;
    }
  } else if (
    kind === KIND.DOCUMENT_CHECKBOX_ACHIEVEMENT ||
    kind === KIND.OA_ACHIEVEMENT
  ) {
    // Response-coupled; not answerable studentless (see file header).
    context[key] = [];
    context["grouped_" + key] = {};
  } else if (kind === KIND.BOOL) {
    // get_context stores True/""; improve_context_rendering overwrites it with
    // the field text for non-reeval docs only.
    if (isReeval) context[key] = isTruthyBool(value);
    else context[key] = isTruthyBool(value) ? field.text : "";
  } else if (kind === KIND.TEXT_EJES_ACHIEVEMENT) {
    context[key] = typeof value === "string" ? value : "";
  } else {
    // STRING / TEXT / TEXT_OPTIONS / select stored as plain text.
    if (isReeval) {
      context[key] =
        value == null ? "" : typeof value === "string" ? value.replace(/\n/g, " ") : value;
    } else if (isTruthyBool(value)) {
      context[key] = field.text;
    } else {
      context[key] = value == null ? "" : value;
    }
  }
}

// `extras`: school-specific "dimensiones" grouped by the area header slug.
// Port of the tail of improve_context_rendering — built AFTER per-field
// resolution because it reads the resolved indicators (context[dataKey]).
function buildExtras(documentTemplate, schoolId, context) {
  const extras = {};
  const pages = (documentTemplate && documentTemplate.pages) || [];
  pages.forEach((page) => {
    let currentSlug = null;
    const rows = (page.rows || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    rows.forEach((row) => {
      const fields = (row.fields || []).filter((f) => f && f.is_active !== false);
      const firstField = fields[0];
      if (!firstField) return;
      if (firstField.is_title && firstField.style === 3) {
        let headerText = firstField.text || firstField.name || "";
        headerText = headerText.replace(/^[IVX]+\.-?\s*/, "");
        currentSlug = slugifyUpper(headerText);
        if (!extras[currentSlug]) extras[currentSlug] = [];
        return;
      }
      if (
        currentSlug &&
        row.school != null &&
        String(row.school) === String(schoolId)
      ) {
        const dataField = fields.find(
          (f) =>
            f.kind === KIND.CUSTOM_CHECKBOX ||
            f.kind === KIND.DOCUMENT_CHECKBOX_ACHIEVEMENT
        );
        if (!dataField || !dataField.key) return;
        const labelField = fields.find((f) => f.kind === KIND.NON_INTERACTABLE);
        const label =
          (labelField && (labelField.text || labelField.name)) ||
          dataField.name ||
          "";
        if (label) {
          const indicators = context[dataField.key] || [];
          if (indicators.length) {
            extras[currentSlug].push({ label, indicators });
          }
        }
      }
    });
  });
  return extras;
}

// documentTemplate: the serialized template (pages/rows/fields).
// fieldValues: the live answers (DocumentOnline.get_context shape).
// documentTypeName: for the "FU de reevaluación online" special-casing.
// schoolId: the document type's school, for the `extras` dimensions.
export function buildRenderContext(
  documentTemplate,
  fieldValues,
  documentTypeName,
  schoolId
) {
  const context = {};
  const values = fieldValues || {};
  const isReeval = documentTypeName === FU_REEVAL;
  const handled = new Set();
  const customPros = {};

  iterFields(documentTemplate).forEach((field) => {
    const key = field.key;
    if (!key || handled.has(key)) return;
    handled.add(key);
    resolveField(context, field, values[key], isReeval, customPros);
  });

  // Custom (non-user) professionals go after the selected platform users.
  Object.keys(customPros).forEach((target) => {
    context[target] = (context[target] || []).concat(customPros[target]);
  });

  // TEXT_EJES_ACHIEVEMENT carries a sibling `${key}_achievements` map locally
  // (set by the modal's setAchievement); surface it as get_context does.
  iterFields(documentTemplate).forEach((field) => {
    if (field.kind !== KIND.TEXT_EJES_ACHIEVEMENT || !field.key) return;
    const ach = values[`${field.key}_achievements`];
    if (ach && typeof ach === "object") {
      if (!context.achievements) context.achievements = {};
      context.achievements[field.key] = ach;
    }
  });

  // Pass through any answered key whose field definition wasn't loaded
  // (e.g. a value set before its page rows were fetched) as a plain value, so
  // simple text answers are never dropped.
  Object.keys(values).forEach((key) => {
    if (!handled.has(key) && !(key in context)) {
      const v = values[key];
      if (typeof v === "string" || typeof v === "number") context[key] = v;
    }
  });

  context.extras = buildExtras(documentTemplate, schoolId, context);

  return context;
}

export default { buildRenderContext };
